import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import {
	CheckIcon,
	Combobox,
	Group,
	type MantineSize,
	Pill,
	PillsInput,
	useCombobox,
} from '@mantine/core';
import { type ReactNode, useState } from 'react';

export interface CreatableMultiSelectItem {
	value: string;
	label: string;
}

interface Props {
	value: string[];
	onChange: (value: string[]) => void;
	data: CreatableMultiSelectItem[];
	/** Called when the "create" option is submitted; return the value to add. */
	onCreate: (query: string) => CreatableMultiSelectItem | string | void;
	getCreateLabel?: (query: string) => ReactNode;
	/** Defaults to: non-empty query with no case-insensitive label match. */
	shouldCreate?: (query: string, data: CreatableMultiSelectItem[]) => boolean;
	label?: ReactNode;
	placeholder?: string;
	size?: MantineSize;
	disabled?: boolean;
	clearable?: boolean;
	'data-testid'?: string;
}

const CREATE_VALUE = '$create';

/**
 * v7 Combobox-based replacement for the removed `creatable` MultiSelect (the
 * multi-value analogue of CreatableSelect). Controlled via value/onChange; the
 * "create new" option calls `onCreate(query)` and adds whatever value it returns.
 */
export const CreatableMultiSelect = ({
	value,
	onChange,
	data,
	onCreate,
	getCreateLabel,
	shouldCreate,
	label,
	placeholder,
	size = 'md',
	disabled,
	clearable,
	'data-testid': dataTestId,
}: Props) => {
	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});
	const [search, setSearch] = useState('');

	const labelFor = (val: string) => data.find((item) => item.value === val)?.label ?? val;

	const trimmed = search.trim();
	const filtered =
		trimmed.length === 0
			? data
			: data.filter((item) => item.label.toLowerCase().includes(trimmed.toLowerCase()));

	const canCreate = shouldCreate
		? shouldCreate(search, data)
		: trimmed.length > 0 &&
			!data.some((item) => item.label.toLowerCase() === trimmed.toLowerCase());

	const handleRemove = (val: string) => onChange(value.filter((v) => v !== val));

	const handleSubmit = (submitted: string) => {
		if (submitted === CREATE_VALUE) {
			const created = onCreate(search);
			const newValue = typeof created === 'string' ? created : created?.value;
			if (newValue != null && !value.includes(newValue)) {
				onChange([...value, newValue]);
			}
		} else {
			onChange(
				value.includes(submitted)
					? value.filter((v) => v !== submitted)
					: [...value, submitted]
			);
		}
		setSearch('');
	};

	const pills = value.map((val) => (
		<Pill key={val} withRemoveButton onRemove={() => handleRemove(val)} disabled={disabled}>
			{labelFor(val)}
		</Pill>
	));

	return (
		<Combobox
			store={combobox}
			withinPortal
			zIndex={Z_INDEX.GLOBAL_MENU}
			onOptionSubmit={handleSubmit}
		>
			<Combobox.DropdownTarget>
				<PillsInput
					label={label}
					size={size}
					disabled={disabled}
					data-testid={dataTestId}
					pointer
					onClick={() => combobox.openDropdown()}
					rightSection={
						clearable && value.length > 0 ? (
							<Combobox.ClearButton onClear={() => onChange([])} />
						) : (
							<Icon variant="caret-down" size="lg" />
						)
					}
				>
					<Pill.Group>
						{pills}
						<Combobox.EventsTarget>
							<PillsInput.Field
								value={search}
								placeholder={placeholder}
								disabled={disabled}
								onFocus={() => combobox.openDropdown()}
								onBlur={() => combobox.closeDropdown()}
								onChange={(event) => {
									combobox.openDropdown();
									combobox.updateSelectedOptionIndex();
									setSearch(event.currentTarget.value);
								}}
								onKeyDown={(event) => {
									if (
										event.key === 'Backspace' &&
										search.length === 0 &&
										value.length > 0
									) {
										event.preventDefault();
										handleRemove(value[value.length - 1]);
									}
								}}
							/>
						</Combobox.EventsTarget>
					</Pill.Group>
				</PillsInput>
			</Combobox.DropdownTarget>
			<Combobox.Dropdown>
				<Combobox.Options>
					{filtered.map((item) => (
						<Combobox.Option
							value={item.value}
							key={item.value}
							active={value.includes(item.value)}
						>
							<Group gap="sm">
								{value.includes(item.value) ? <CheckIcon size={12} /> : null}
								<span>{item.label}</span>
							</Group>
						</Combobox.Option>
					))}
					{canCreate && (
						<Combobox.Option value={CREATE_VALUE}>
							{getCreateLabel ? getCreateLabel(search) : `+ Create "${search}"`}
						</Combobox.Option>
					)}
					{filtered.length === 0 && !canCreate && (
						<Combobox.Empty>Nothing found</Combobox.Empty>
					)}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};
